import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush
} from 'recharts';
import { getYAxisLabel } from '@shared/components/GraphLabels';
import AggregatePerformanceTooltip from './AggregatePerformanceTooltip';
import constants from '@shared-styles/constants.module.scss';
import { calculateDomains } from '../utils/domainCalculation';

/**
 * @typedef {object} AggregatePerformanceExpirationData
 * @property {number} [year]
 * @property {number} [quarter]
 */

/**
 * @typedef {object} AggregatePerformanceQuarterlyData
 * @property {string} quarter
 * @property {number} [average]
 * @property {number} [median]
 * @property {number} [count]
 */

/**
 * @typedef {object} AggregatePerformanceClassificationMetric
 * @property {string} classificationMetricName
 * @property {string} measurementUnit
 * @property {AggregatePerformanceExpirationData} [expirationData]
 * @property {AggregatePerformanceQuarterlyData[]} quarterlyData
 */

/**
 * @typedef {object} AggregatePerformanceGraphProps
 * @property {AggregatePerformanceClassificationMetric} classificationMetric
 */

/**
 * @param {AggregatePerformanceGraphProps} props
 */
const AggregatePerformanceGraph = props => {
  const { classificationMetric } = props;

  const domains = calculateDomains(classificationMetric.quarterlyData);

  const renderTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;

    return <AggregatePerformanceTooltip data={payload[0].payload} />;
  };

  return (
    <div className='aggregate-performance-graph' data-testid='aggregate-performance-graph'>
      <h3>{classificationMetric.classificationMetricName}</h3>

      {classificationMetric.expirationData && (
        <div className='warning-message' data-testid='expiring-metric-message'>
          <span>{getExpiringMetricMessage(classificationMetric.expirationData)}</span>
        </div>
      )}

      <ResponsiveContainer width='100%' height='100%'>
        <BarChart
          margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
          data={classificationMetric.quarterlyData}>
          <CartesianGrid strokeDasharray='3 3' yAxisId='left' />
          <CartesianGrid strokeDasharray='3 3' yAxisId='right' />
          <XAxis dataKey='quarter' />
          <YAxis
            yAxisId='left'
            orientation='left'
            stroke={constants.isacaMediumBlue}
            domain={domains.average}
            scale='linear'>
            {getYAxisLabel(classificationMetric.measurementUnit)}
          </YAxis>
          <YAxis
            yAxisId='right'
            orientation='right'
            stroke={constants.isacaOrange}
            domain={domains.median}
            scale='linear'
          />
          <Tooltip content={renderTooltip} />
          <Legend align='center' />
          <Bar dataKey='average' fill={constants.isacaMediumBlue} name='Average' yAxisId='left' />
          <Bar dataKey='median' fill={constants.isacaOrange} name='Median' yAxisId='right' />
          <Brush
            dataKey='quarter'
            stroke={constants.isacaMediumBlue}
            fill={constants.isacaMediumBlueWithLightOpacity}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const getExpiringMetricMessage = expirationData =>
  `Not enough sites reporting this metric. Graph will expire after ${expirationData.year}` +
  ` Quarter ${expirationData.quarter}`;

export default AggregatePerformanceGraph;
